import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';

import { getImpersonatorName, stopImpersonation } from '~services/sessionTokens';
import { useStores } from '~store';

const ImpersonationPill = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const adminName = getImpersonatorName();
  const impersonatedCompany = userStore.userCompany.legalName;
  const truncatingSx = { overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
  const strongSx = { ...truncatingSx, fontSize: 'inherit', fontWeight: 600 };
  const mutedSx = {
    ...truncatingSx,
    fontSize: 'inherit',
    fontWeight: 500,
    color: theme.semantic.elevatedStateInkMuted,
  };

  return (
    <Box
      data-test-id="impersonation-pill"
      sx={{
        display: { xs: 'none', md: 'inline-flex' },
        alignItems: 'center',
        gap: '10px',
        maxWidth: '100%',
        height: '36px',
        padding: '0 8px 0 14px',
        borderRadius: '999px',
        backgroundColor: theme.semantic.elevatedStateBg,
        border: `1px solid ${theme.semantic.elevatedStateBorder}`,
        color: theme.semantic.elevatedStateInk,
        fontSize: '14px',
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          color: theme.semantic.elevatedStateInk,
        }}
      >
        <Box
          sx={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: 'currentColor',
            animation: 'imp-pulse 2.2s ease-in-out infinite',
            '@keyframes imp-pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <VisibilityOutlined sx={{ fontSize: '16px' }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography component="span" data-sentry-mask sx={strongSx}>
          {adminName}
        </Typography>

        <Typography component="span" sx={mutedSx}>
          {t('page_headings.impersonating_connector')}
        </Typography>

        <Typography component="span" data-sentry-mask sx={strongSx}>
          {userStore.user.fullName}
        </Typography>

        {impersonatedCompany && (
          <>
            <Typography component="span" sx={mutedSx}>
              {t('page_headings.impersonating_in')}
            </Typography>

            <Typography component="span" sx={strongSx}>
              {impersonatedCompany}
            </Typography>
          </>
        )}
      </Box>

      <Box
        aria-label={t('page_headings.exit_impersonation_aria')}
        component="button"
        onClick={stopImpersonation}
        type="button"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
          height: '28px',
          px: '8px',
          border: `1px solid ${theme.semantic.elevatedStateInk}`,
          borderRadius: '999px',
          backgroundColor: 'transparent',
          color: theme.semantic.elevatedStateInk,
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 700,
          '&:hover': {
            backgroundColor: theme.semantic.elevatedStateBorder,
          },
        }}
      >
        <CloseIcon sx={{ fontSize: '12px' }} />
        {t('page_headings.exit_impersonation')}
      </Box>
    </Box>
  );
};

export default observer(ImpersonationPill);
